"use client";

import { useMemo, useState } from "react";
import { formOptions } from "@tanstack/react-form";
import { useAppForm } from "../shared/hooks/form";
import { useTranslations } from "next-intl";
import { VC_FORM_DEFAULTS, fieldConfig } from "./constants";
import {
  requiredString,
  emailValidator,
  urlValidator,
  countryValidator,
  termsAgreedValidator,
} from "../shared/validators";
import { createValidationMessages, enrichWithUTMs } from "../shared/utils";
import type { VCFormPayload } from "./types";
import type { HubSpotSubmitResult } from "@/app/_api/hubspot/shared/types";
import { SubmitButton } from "../shared/SubmitButton";
import { useAnalytics } from "@ui/hooks/useAnalytics";
import { EventName } from "@mw/shared/analyticsEventNames";
import { logger } from "@utilities/logger";
import { Button } from "@ui/components/core/Button";

const formOpts = formOptions({
  defaultValues: VC_FORM_DEFAULTS,
});

export interface VCFormProps {
  onSubmit: (payload: VCFormPayload) => Promise<HubSpotSubmitResult>;
  onSuccess?: () => void;
  onCancel?: () => void;
}

export function VCForm({ onSubmit, onSuccess, onCancel }: VCFormProps) {
  const t = useTranslations("FormsClientKeys.vc_form");
  const tValidation = useTranslations("ClientKeys.forms.validation");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const { captureEvent } = useAnalytics();

  const messages = useMemo(() => createValidationMessages(tValidation), [tValidation]);
  const validators = useMemo(
    () => ({
      name: requiredString(messages, 100),
      email: emailValidator(messages),
      fundName: requiredString(messages, 200),
      fundWebsite: urlValidator(messages),
      location: countryValidator(messages),
      consent: termsAgreedValidator(messages),
    }),
    [messages],
  );

  const legalLinkClassName = "tw-underline tw-underline-offset-2 hover:tw-no-underline";
  const privacyPolicyUrl = "https://elevenlabs.io/privacy-policy";

  const form = useAppForm({
    ...formOpts,
    onSubmit: async ({ value }) => {
      setSubmitError(null);

      const payload: VCFormPayload = enrichWithUTMs(value);

      try {
        const result = await onSubmit(payload);
        if (result.success) {
          captureEvent(EventName.mw_vc_form_submitted, {});
          onSuccess?.();
        } else {
          setSubmitError(result.error || t("errors.submission_failed"));
        }
      } catch (error) {
        logger.error(error instanceof Error ? error : null, "Failed to submit VC application");
        setSubmitError(t("errors.submission_failed"));
        throw error;
      }
    },
  });

  return (
    <div className='tw-w-full'>
      <form
        onSubmit={e => {
          e.preventDefault();
          void form.handleSubmit();
        }}
      >
        <form.AppField
          name={fieldConfig.name.name}
          validators={{ onMount: validators.name, onChange: validators.name }}
        >
          {field => (
            <field.Input
              name={fieldConfig.name.name}
              label={t("fields.name.label")}
              placeholder={t("fields.name.placeholder")}
              required={fieldConfig.name.required}
            />
          )}
        </form.AppField>

        <form.AppField
          name={fieldConfig.email.name}
          validators={{ onMount: validators.email, onChange: validators.email }}
        >
          {field => (
            <field.Input
              name={fieldConfig.email.name}
              label={t("fields.email.label")}
              placeholder={t("fields.email.placeholder")}
              type='email'
              required={fieldConfig.email.required}
            />
          )}
        </form.AppField>

        <form.AppField
          name={fieldConfig.fundName.name}
          validators={{ onMount: validators.fundName, onChange: validators.fundName }}
        >
          {field => (
            <field.Input
              name={fieldConfig.fundName.name}
              label={t("fields.fund_name.label")}
              placeholder={t("fields.fund_name.placeholder")}
              required={fieldConfig.fundName.required}
            />
          )}
        </form.AppField>

        <form.AppField
          name={fieldConfig.fundWebsite.name}
          validators={{ onMount: validators.fundWebsite, onChange: validators.fundWebsite }}
        >
          {field => (
            <field.Input
              name={fieldConfig.fundWebsite.name}
              label={t("fields.fund_website.label")}
              placeholder={t("fields.fund_website.placeholder")}
              required={fieldConfig.fundWebsite.required}
            />
          )}
        </form.AppField>

        <form.AppField
          name={fieldConfig.location.name}
          validators={{ onMount: validators.location, onChange: validators.location }}
        >
          {field => (
            <field.CountrySelect
              name={fieldConfig.location.name}
              label={t("fields.location.label")}
              required={fieldConfig.location.required}
              showTooltip={false}
            />
          )}
        </form.AppField>

        <form.AppField
          name={fieldConfig.consent.name}
          validators={{ onMount: validators.consent, onChange: validators.consent }}
        >
          {field => (
            <field.FormCheckbox
              name={fieldConfig.consent.name}
              label={t.rich("fields.consent.label", {
                privacyPolicy: chunks => (
                  <a href={privacyPolicyUrl} target='_blank' rel='noopener noreferrer' className={legalLinkClassName}>
                    {chunks}
                  </a>
                ),
              })}
              required={fieldConfig.consent.required}
            />
          )}
        </form.AppField>

        <form.Subscribe selector={state => ({ canSubmit: state.canSubmit, isSubmitting: state.isSubmitting })}>
          {({ canSubmit, isSubmitting }) => (
            <div className='tw-flex tw-items-end tw-gap-4'>
              {onCancel && (
                <Button type='button' variant='secondary' size='lg' className='tw-flex-1' onClick={onCancel}>
                  {t("buttons.cancel")}
                </Button>
              )}
              <div className={onCancel ? "tw-flex-1" : "tw-w-full"}>
                <SubmitButton
                  canSubmit={canSubmit}
                  isSubmitting={isSubmitting}
                  submitLabel={t("buttons.submit")}
                  submittingLabel={t("buttons.submitting")}
                  submitError={submitError}
                />
              </div>
            </div>
          )}
        </form.Subscribe>
      </form>
    </div>
  );
}
